import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Calculator, LogOut, ArrowLeft, Save, Plus, Trash2, Loader2, Palette } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMarginColors = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ranges, setRanges] = useState([
    { min: 0, max: 5, bg: "#FEF2F2", text: "#DC2626", label: "Crítico" },
    { min: 5, max: 10, bg: "#FFF7ED", text: "#EA580C", label: "Baixo" },
    { min: 10, max: 20, bg: "#FEFCE8", text: "#CA8A04", label: "Médio" },
    { min: 20, max: null, bg: "#F0FDF4", text: "#16A34A", label: "Bom" },
  ]);

  useEffect(() => { loadColors(); }, []);

  const loadColors = async () => {
    try {
      const res = await axios.get(`${API}/margin-colors`, { headers: getAuthHeaders() });
      if (res.data?.ranges) setRanges(res.data.ranges);
    } catch (e) { console.error("[AdminMarginColors] load:", e); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/margin-colors`, { ranges }, { headers: getAuthHeaders() });
      toast.success("Cores da margem guardadas!");
    } catch { toast.error("Erro ao guardar"); }
    finally { setSaving(false); }
  };

  const addRange = () => {
    setRanges([...ranges, { min: null, max: null, bg: "#F0FDF4", text: "#16A34A", label: "" }]);
  };

  const removeRange = (i) => {
    if (ranges.length <= 1) return;
    setRanges(ranges.filter((_, idx) => idx !== i));
  };

  const updateRange = (i, field, value) => {
    const next = [...ranges];
    if (["min", "max"].includes(field)) {
      // Empty string → null (no default value)
      next[i][field] = value === "" ? null : parseFloat(value);
    } else {
      next[i][field] = value;
    }
    setRanges(next);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-margin-colors-page">
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-manrope font-bold text-lg text-foreground">Evolutt</span>
              <p className="text-xs text-muted-foreground">Cores da Margem</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }}>
              <LogOut className="w-4 h-4 mr-2" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin"><Button variant="outline" size="sm"><ArrowLeft className="w-4 h-4 mr-2" /> Voltar</Button></Link>
            <div>
              <h1 className="font-manrope font-extrabold text-2xl flex items-center gap-2">
                <Palette className="w-6 h-6 text-purple-500" /> Cores da Margem
              </h1>
              <p className="text-muted-foreground text-sm">Configurar cores por faixa de margem (%)</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground" data-testid="save-margin-colors-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar
          </Button>
        </div>

        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="font-semibold">Faixas de Margem</Label>
              <Button variant="outline" size="sm" onClick={addRange}><Plus className="w-4 h-4 mr-1" /> Faixa</Button>
            </div>

            {ranges.map((r, i) => (
              <div key={i} className="p-4 rounded-lg border-2" style={{ backgroundColor: r.bg, borderColor: r.text + "40" }}>
                <div className="grid grid-cols-7 gap-3 items-end">
                  <div>
                    <Label className="text-xs">Margem Mín (%)</Label>
                    <Input type="number" value={r.min === null ? "" : r.min} onChange={e => updateRange(i, "min", e.target.value)} placeholder="—" className="font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs">Margem Máx (%)</Label>
                    <Input type="number" value={r.max === null ? "" : r.max} onChange={e => updateRange(i, "max", e.target.value)} placeholder="sem limite" className="font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs">Label</Label>
                    <Input value={r.label} onChange={e => updateRange(i, "label", e.target.value)} placeholder="Ex: Bom" />
                  </div>
                  <div>
                    <Label className="text-xs">Cor Fundo</Label>
                    <div className="flex items-center gap-1">
                      <input type="color" value={r.bg} onChange={e => updateRange(i, "bg", e.target.value)} className="w-8 h-8 rounded cursor-pointer border" />
                      <Input value={r.bg} onChange={e => updateRange(i, "bg", e.target.value)} className="font-mono text-xs" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Cor Texto</Label>
                    <div className="flex items-center gap-1">
                      <input type="color" value={r.text} onChange={e => updateRange(i, "text", e.target.value)} className="w-8 h-8 rounded cursor-pointer border" />
                      <Input value={r.text} onChange={e => updateRange(i, "text", e.target.value)} className="font-mono text-xs" />
                    </div>
                  </div>
                  <div className="text-center">
                    <Label className="text-xs">Preview</Label>
                    <div className="p-2 rounded text-center text-sm font-bold" style={{ backgroundColor: r.bg, color: r.text }}>
                      {r.label || "—"} {r.min ?? "—"}%-{r.max ?? "max"}
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => removeRange(i)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminMarginColors;
